import { createGlobalStyle } from "styled-components";
import { Device } from "../../../components/device";
import { isBrowserContext } from "../../../util/ContextHelper";

if (isBrowserContext()) {
    import("webfontloader").then(({ default: WebFont }) => {
        //Font families import
        WebFont.load({
            google: {
                families: ["Josefin+Sans:400,600,700&display=swap", "sans-serif"],
            },
        });
        WebFont.load({
            google: {
                families: ["Lato:400,700&display=swap", "sans-serif"],
            },
        });
    });
}

const GlobalStyle = createGlobalStyle`
    &:root {
        --font-josefin-sans: 'Josefin Sans', sans-serif;
        --font-lato: 'Lato', sans-serif;

        --color-white: #FFFFFF;
        --color-gray-10: #f4f4f4;
        --color-gray-20: #ebebeb;
        --color-gray-30: #bfbfbf;
        --color-gray-40: #999999;
        --color-gray-50: #616161;
        --color-gray-60: #2e2e2e;
        --color-black: #000000;
        --color-green: #2F855A;
        --color-red: #B00020;

        --text-xs: 0.7333rem; //11px
        --text-sm: 0.8667rem; //13px
        --text-base: 15px; //15px
        --text-lg: 1.133rem; //17px
        --text-xl:  1.267rem; //19px
        --text-2xl: 1.4rem; //21px
        --text-3xl: 1.667rem; //25px
        --text-4xl: 2.333rem; //35px
        --text-5xl: 3.333rem; //50px
        --text-6xl: 4.667rem; //70px

        --leading-none: 1;
        --leading-tighter: 1.125;
        --leading-tight: 1.25;
        --leading-snug: 1.375;
        --leading-normal: 1.5;
        --leading-relaxed: 1.625;
        --leading-loose: 2;

        --shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
        --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        --shadow-inner: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
        --shadow-outline: 0 0 0 3px rgba(66, 153, 225, 0.5);
        --shadow-none: none;

        --form-input-color: var(--color-gray-60);
        --form-input-color-focus: var(--color-black);
        --form-input-border-color: var(--color-gray-30);
        --form-input-border-color-focus: var(--color-black);
        --form-input-border-width: 2px;
        --form-input-invalid-color: var(--color-red);
        --form-input-valid-color: currentColor;

        --scrollbar-width: 3px;
        --scrollbar-track-color: var(--color-gray-10);
        --scrollbar-thumb-color: var(--color-gray-50);
        --scrollbar-thumb-focus-color: var(--color-gray-60);
    }

    html {
        min-height: 100%;
        margin: 0;
        padding: 0;
        scroll-behavior: smooth;
        -webkit-tap-highlight-color: transparent;
    }
    body {
        font-family: var(--font-lato, 'Lato', sans-serif);
        font-size: var(--text-base, 15px);
        line-height: var(--leading-normal, 1.5);
        min-height: 100%;
        min-width: 320px;
        margin: 0;
        padding: 0;
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    .container {
        ${Device.tabletLandscape} {
            max-width: 90vw;
        }
        ${Device.laptop} {
            max-width: 75vw;
        }
        ${Device.desktopLG} {
            max-width: 1600px;
        }
    }

    .font-josefin-sans {
        font-family: var(--font-josefin-sans, 'Josefin Sans', sans-serif) !important;
    }
    .font-lato {
        font-family: var(--font-lato, 'Lato', sans-serif) !important;
    }

    //Typography
    h1, .h1,
    h2, .h2 {
        font-family: 'Josefin Sans', 'Lato', sans-serif;
        font-size: var(--text-3xl, 1.875rem);
        line-height: var(--leading-tighter, 1.125);
        margin-bottom: 1rem;
        font-weight: bold;
        letter-spacing: 0.05em;
        overflow-wrap: break-word;
        ${Device.tablet} {
            font-size: var(--text-4xl, 2.333rem);
            margin-bottom: 2rem;
        }
        ${Device.laptop} {
            font-size: var(--text-5xl, 3.333rem);
        }
    }
    h3, .h3 {
        font-family: 'Josefin Sans', 'Lato', sans-serif;
        font-size: var(--text-2xl, 1.5rem);
        line-height: var(--leading-tighter, 1.125);
        margin-bottom: .5rem;
        font-weight: bold;
        letter-spacing: 0.025em;
        overflow-wrap: break-word;
        ${Device.tablet} {
            font-size: var(--text-3xl, 1.875rem);
        }
        ${Device.laptop} {
            font-size: var(--text-4xl, 2.333rem);
        }
    }
    h4, .h4 {
        font-family: 'Josefin Sans', 'Lato', sans-serif;
        font-size: var(--text-xl, 1.25rem);
        line-height: var(--leading-tighter, 1.125);
        margin-bottom: .5rem;
        text-transform: unset;
        letter-spacing: 0.025em;
        overflow-wrap: break-word;
        ${Device.tablet} {
            font-size: var(--text-4xl, 2.333rem);
        }
        ${Device.laptop} {
            font-size: var(--text-3xl, 1.667rem);
        }
    }
    h5, .h5 {
        font-family: 'Josefin Sans', 'Lato', sans-serif;
        font-size: var(--text-lg, 1.125rem);
        font-weight: semibold;
        line-height: var(--leading-tighter, 1.125);
        letter-spacing: 0.025em;
        margin-bottom: .5rem;
        overflow-wrap: break-word;
        ${Device.tablet} {
            font-size: var(--text-xl, 1.25rem);
        }
        ${Device.laptop} {
            font-size: var(--text-2xl, 1.4rem);
        }
    }
    h6, .h6 {
        font-family: 'Josefin Sans', 'Lato', sans-serif;
        font-size: var(--text-xs, .7333rem);
        font-weight: bold;
        letter-spacing: 0.025em;
        line-height: var(--leading-tighter, 1.125);
        margin-bottom: .5rem;
        opacity: .66;
        text-transform: uppercase;
        overflow-wrap: break-word;
    }
    p {
        margin: 0 0 1rem;
        font-size: 1.06rem;

        ${Device.tablet} {
            font-size: unset;
        }
    }

    .text--manchet {
        font-size: var(--text-xl, 1.267rem);
        letter-spacing: 0.025em;
        margin-bottom: 2rem;
        line-height: var(--leading-relaxed, 1.625);
    }
    .text--subheader {
        font-size: var(--text-lg, 1.133rem);
        font-weight: bold;
        margin-bottom: .5rem;
        letter-spacing: 0.025em;
        line-height: var(--leading-tighter, 1.125);
    }
    .text--standard {
        font-size: var(--text-base, 1.1rem);

        ${Device.tablet} {
            font-size: var(--text-base, 1rem);
        }
    }
    small, .small,
    .text--small {
        font-size: var(--text-sm, .875rem);
    }
    .text-xSmall {
        font-size: var(--text-xs, .7333rem);
        letter-spacing: 0.025em;
    }

    //Colors
    .bg-white { background-color: var(--color-white, white); }
    .bg-gray-10 { background-color: var(--color-gray-10, #f4f4f4); }
    .bg-gray-20 { background-color: var(--color-gray-20, #ebebeb); }
    .bg-gray-30 { background-color: var(--color-gray-30, #bfbfbf); }
    .bg-gray-40 { background-color: var(--color-gray-40, #999999); }
    .bg-gray-50 { background-color: var(--color-gray-50, #616161); }
    .bg-gray-60 { background-color: var(--color-gray-60, #2e2e2e); }
    .bg-black { background-color: var(--color-black, black); }
    .bg-green { background-color: var(--color-green, #2F855A); }
    .bg-red { background-color: var(--color-red, #B00020); }

    .text-white { color: var(--color-white, white); }
    .text-gray-10 { color: var(--color-gray-10, #f4f4f4); }
    .text-gray-20 { color: var(--color-gray-20, #ebebeb); }
    .text-gray-30 { color: var(--color-gray-30, #bfbfbf); }
    .text-gray-40 { color: var(--color-gray-40, #999999); }
    .text-gray-50 { color: var(--color-gray-50, #616161); }
    .text-gray-60 { color: var(--color-gray-60, #2e2e2e); }
    .text-black { color: var(--color-black, black); }
    .text-green { color: var(--color-green, #2F855A); }
    .text-red { color: var(--color-red, #B00020); }

    //Font utilities
    .font-hairline { font-weight: 100; }
    .font-thin { font-weight: 200; }
    .font-light { font-weight: 300; }
    .font-normal { font-weight: 400; }
    .font-medium { font-weight: 500; }
    .font-semibold { font-weight: 600; }
    .font-bold { font-weight: 700; }
    .font-extrabold { font-weight: 800; }
    .font-black { font-weight: 900; }

    //Scrollbar
    .scrollbar {
        &::-webkit-scrollbar { width: var(--scrollbar-width, 3px);}
        &::-webkit-scrollbar-track { background: var(--scrollbar-track-color, lightgray); }
        &::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb-color, gray); }
        &::-webkit-scrollbar-thumb:hover { background: var(--scrollbar-thumb-focus-color, black); }
    }

/**
 * What the heck is this style doing here?
 * It should be part of the mobile navigation, perhaps others too?
 * This code below needs to be moved.
 */
   .menu-header-btn {
        border: none;
        color: currentColor;
        cursor: pointer;
        display: flex;
        font-size: 1rem;
        font-weight: bold;
        letter-spacing: .075rem;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
        height: var(--app-bar-height, 60px);
        padding: .75rem 1rem;
        position: relative;
        overflow: hidden;
        user-select: none;
        text-transform: uppercase;
        text-decoration: none;
        &:focus {
            outline: none;
        }
    }

   .menu-item-btn {
        border-radius: 0;
        border: none;
        display: flex;
        color: currentColor;
        cursor: pointer;
        font-size: var(--text-small, 13px);
        font-weight: bold;
        letter-spacing: .075rem;
        padding: .5rem 1rem;
        overflow: hidden;
        user-select: none;
        text-decoration: none;
        text-transform: uppercase;
        &:focus {
            outline: none;
        }
        &.active {
            opacity: .5;
        }
       ${Device.mobileMax} {
            align-items: center;
            justify-content: space-between;
            font-size: 1rem;
            padding: .75rem 1rem;
            border: 1px solid transparent;
            width: 100%;
        }
    }

    //Jumplinks scroll fix
    a[id] {
        vertical-align: top;
    }

    //Grid START
    .grid-row {
        margin: 0 auto;
        width: 100%;
        &--w-full {
            padding: 0 !important;
        }
    }

    .grid-row:not(.container) {
        .grid-area--full {
            padding: 0 !important;
        }
    }

    .grid-area {
        align-items: center;
        padding-left: 1rem;
        padding-right: 1rem;
        width: 100%;
        ${Device.mobileMax} {
            padding-bottom: 1.5rem;
        }
        &--half {
            ${Device.tablet} {
                width: 50%;
            }
        }
        &--third {
            ${Device.tablet} {
                width: 33.33%;
            }
        }
        &--fourth {
            ${Device.tablet} {
                width: 25%;
            }
        }
    }
    //Grid END

    
`;

export default GlobalStyle;
